import React from 'react';
import { CITY_SEARCH_FORM_FIELD_NAME_DEFAULT } from '@cp/entities/location';
import { useEventCallback } from '@cp/ds/src/hooks/useEventCallback';
import { useTrackEvent } from 'src/shared/tracking';
// FIXME: upper level import
import { CatalogEvents } from 'src/features/catalog';
import { FormAvailableOnlyFieldName } from 'src/entities/catalog/ui/availableOnly/model';
import { FormFieldName, FormModel } from './form';
import { WatcherProps } from './watcher.interface';
import { SORTING_ITEMS } from './sorters';

export const FORM_FIELD_NAMES_TO_WATCH = [
  FormAvailableOnlyFieldName.Date,
  FormAvailableOnlyFieldName.Start,
  FormAvailableOnlyFieldName.End,
  FormFieldName.Sorters,
] as const;

export const Watcher: React.FC<WatcherProps> = (props) => {
  const { defaultCity, onChangeCity, form, submit, defaultValues } = props;
  const onChangeCityMemoized = useEventCallback(onChangeCity);
  const track = useTrackEvent();

  React.useEffect(() => {
    const city = form.getValues(CITY_SEARCH_FORM_FIELD_NAME_DEFAULT);

    if (defaultCity && defaultCity.id !== city?.id) {
      form.setValue(CITY_SEARCH_FORM_FIELD_NAME_DEFAULT, defaultCity);
    }
  }, [defaultCity, form]);

  React.useEffect(() => {
    const subscription = form.watch((values, info) => {
      if (info.type !== 'change') {
        return;
      }

      /**
       * City changes
       */
      const city = values[CITY_SEARCH_FORM_FIELD_NAME_DEFAULT] as FormModel[typeof CITY_SEARCH_FORM_FIELD_NAME_DEFAULT];

      if (info.name === CITY_SEARCH_FORM_FIELD_NAME_DEFAULT && city && defaultValues[CITY_SEARCH_FORM_FIELD_NAME_DEFAULT]?.id !== city.id) {
        onChangeCityMemoized?.(city);
      }

      /**
       * Filter changes
       */
      if (
        info.name === FormAvailableOnlyFieldName.Date &&
        values[FormAvailableOnlyFieldName.Date] &&
        !values[FormAvailableOnlyFieldName.AvailableOnly]
      ) {
        form.setValue(FormAvailableOnlyFieldName.AvailableOnly, true);
        track(CatalogEvents.FilterDateChange());
      }

      if (info.name === FormAvailableOnlyFieldName.End && !values[FormAvailableOnlyFieldName.End]) {
        track(CatalogEvents.FilterTimeChange());
      }

      if (info.name === FormAvailableOnlyFieldName.AvailableOnly && values[FormAvailableOnlyFieldName.AvailableOnly]) {
        track(CatalogEvents.FilterShowAvailable());
      }

      /**
       * Sorting changes
       */
      if (info.name === FormFieldName.Sorters && values[FormFieldName.Sorters]) {
        const sort = SORTING_ITEMS.find((i) => i.value === values[FormFieldName.Sorters])?.label;
        if (sort) {
          track(CatalogEvents.SortingChange({ sort }));
        }
      }

      const hasChanges = FORM_FIELD_NAMES_TO_WATCH.find((fieldName) => values[fieldName] !== defaultValues[fieldName]);
      if (hasChanges) {
        submit();
      }
    });
    return () => subscription.unsubscribe();
  }, [defaultValues, form, onChangeCityMemoized, submit, track]);

  return null;
};
